/* @img-migrated */
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Share2, CheckCircle2, XCircle, Clock, Play, Filter, Calendar, ImageIcon, ExternalLink, ChevronDown, ChevronUp, Trash2
} from 'lucide-react';
import { Button } from '../../components/ui/button';

import Img from '../../components/Img';
import { resolveImg } from '../../lib/images';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusOptions = [
  { value: 'pending', label: 'Pending', icon: Clock, color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  { value: 'in_progress', label: 'In Progress', icon: Play, color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { value: 'approved', label: 'Approved', icon: CheckCircle2, color: 'bg-green-100 text-green-700 border-green-200' },
  { value: 'completed', label: 'Completed', icon: CheckCircle2, color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  { value: 'declined', label: 'Declined', icon: XCircle, color: 'bg-red-100 text-red-700 border-red-200' },
];

const SocialRequestsReviewPage = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [expandedId, setExpandedId] = useState(null);
  const [processing, setProcessing] = useState({});

  useEffect(() => {
    fetchRequests();
     
  }, [filter]);

  const fetchRequests = async () => {
    try {
      const url = filter ? `${API}/admin/social-requests?status=${filter}` : `${API}/admin/social-requests`;
      const res = await axios.get(url, { withCredentials: true });
      setRequests(res.data);
    } catch (err) {
      // error handled silently
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, newStatus) => {
    setProcessing(prev => ({ ...prev, [id]: true }));
    try {
      await axios.put(`${API}/admin/social-requests/${id}?status=${newStatus}`, {}, { withCredentials: true });
      toast.success(`Request ${newStatus.replace('_', ' ')}!`);
      fetchRequests();
    } catch (err) {
      toast.error('Failed to update');
    } finally {
      setProcessing(prev => ({ ...prev, [id]: false }));
    }
  };

  const handleDeleteSocial = async (id) => {
    if (!window.confirm('Are you sure you want to delete this social media request?')) return;
    try {
      await axios.delete(`${API}/admin/social-requests/${id}`, { withCredentials: true });
      toast.success('Request deleted');
      fetchRequests();
    } catch (err) { toast.error('Failed to delete'); }
  };

  const getStatus = (val) => statusOptions.find(s => s.value === val) || statusOptions[0];

  return (
    <div className="p-6 lg:p-8" data-testid="admin-social-review">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#002249]">Social Media Requests</h1>
        <p className="text-slate-600 text-sm mt-1">Review and manage social media promotion requests from members.</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <Filter className="w-4 h-4 text-slate-500" />
        {['pending', 'in_progress', 'approved', 'completed', 'declined', ''].map(f => (
          <button
            key={f}
            onClick={() => { setFilter(f); setLoading(true); }}
            className={`px-3 py-1.5 rounded-sm text-xs font-medium transition-all ${filter === f ? 'bg-[#002249] text-white' : 'bg-white border border-slate-200 text-slate-600'}`}
            data-testid={`filter-${f || 'all'}`}
          >
            {f === '' ? 'All' : f.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="animate-spin w-8 h-8 border-4 border-[#002249] border-t-transparent rounded-full" /></div>
      ) : requests.length === 0 ? (
        <div className="text-center py-16 text-slate-500">
          <Share2 className="w-16 h-16 mx-auto mb-4 text-slate-300" />
          <p className="text-lg font-medium">No {filter ? filter.replace('_', ' ') + ' ' : ''}requests</p>
        </div>
      ) : (
        <div className="space-y-4">
          {requests.map((req, index) => {
            const status = getStatus(req.status);
            const isExpanded = expandedId === req.id;
            return (
              <motion.div
                key={req.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.03 * index }}
                className="bg-white border border-slate-200 rounded-sm overflow-hidden"
                data-testid={`social-request-${index}`}
              >
                <div className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="font-bold text-[#002249]">{req.campaign_topic}</h3>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-sm border flex items-center flex-shrink-0 ${status.color}`}>
                          <status.icon className="w-3 h-3 mr-1" />{status.label}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 mb-1">{req.business_name}</p>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
                        <span>By {req.user_name} ({req.user_email})</span>
                        <span className="flex items-center"><Calendar className="w-3 h-3 mr-1" />Desired: {req.desired_date ? new Date(req.desired_date + 'T00:00:00').toLocaleDateString() : 'N/A'}</span>
                        <span>Submitted: {new Date(req.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDeleteSocial(req.id); }} className="text-red-400 hover:text-red-600" data-testid={`delete-social-${index}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      <button onClick={() => setExpandedId(isExpanded ? null : req.id)} className="text-slate-400 hover:text-slate-600 p-1">
                        {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-t border-slate-200">
                    <div className="p-5 bg-slate-50 space-y-3 text-sm">
                      {req.event_details && <div><span className="font-medium text-slate-600">Event Details:</span><p className="text-slate-700 mt-0.5">{req.event_details}</p></div>}
                      {req.preferred_message && <div><span className="font-medium text-slate-600">Preferred Message:</span><p className="text-slate-700 mt-0.5">{req.preferred_message}</p></div>}
                      {req.cta && <div><span className="font-medium text-slate-600">CTA:</span> <span className="text-slate-700">{req.cta}</span></div>}
                      {req.notes && <div><span className="font-medium text-slate-600">Notes:</span><p className="text-slate-700 mt-0.5">{req.notes}</p></div>}
                      {req.video_url && <div><span className="font-medium text-slate-600">Video:</span> <a href={req.video_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{req.video_url}</a></div>}
                      
                      {/* Images */}
                      {(req.logo_url || (req.images && req.images.length > 0)) && (
                        <div>
                          <span className="font-medium text-slate-600">Assets:</span>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {req.logo_url && (
                              <div className="border border-slate-200 rounded-sm p-1 bg-white">
                                <p className="text-xs text-slate-400 mb-1">Logo</p>
                                <Img variant="business" src={resolveImg(req.logo_url)} alt="Logo" className="w-20 h-20 object-contain" />
                              </div>
                            )}
                            {req.images?.map((img, i) => (
                              <div key={i} className="border border-slate-200 rounded-sm p-1 bg-white">
                                <p className="text-xs text-slate-400 mb-1">Image {i + 1}</p>
                                <Img variant="generic" src={resolveImg(img)} alt={`Campaign ${i}`} className="w-20 h-20 object-cover" />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Status Actions */}
                    <div className="p-5 bg-amber-50/50 border-t border-slate-200">
                      <h4 className="text-sm font-semibold text-slate-700 mb-3">Update Status</h4>
                      <div className="flex flex-wrap gap-2">
                        {statusOptions.filter(s => s.value !== req.status).map(s => (
                          <Button
                            key={s.value}
                            onClick={() => updateStatus(req.id, s.value)}
                            disabled={processing[req.id]}
                            variant="outline"
                            className={`rounded-sm text-xs ${s.value === 'approved' ? 'border-green-300 text-green-700 hover:bg-green-50' : s.value === 'declined' ? 'border-red-300 text-red-700 hover:bg-red-50' : 'border-slate-300'}`}
                            data-testid={`set-${s.value}-${index}`}
                          >
                            <s.icon className="w-3.5 h-3.5 mr-1" />{s.label}
                          </Button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SocialRequestsReviewPage;
